import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  InputGroup,
  InputGroupAddon,
  Menu,
  MenuItem,
  ProgressCircle
} from '@sm/wds-react';

import { IconSearch, IconWarning } from '@sm/wds-icons';

import './autocomplete.scss';

const UNSELECTED_ITEM_INDEX = -1;

const Autocomplete = props => {
  const {
    data,
    onTextChanged,
    onItemSelected,
    text,
    placeholder,
    renderItem,
    onSubmit,
    isLoading,
    icon,
    hasError
  } = props;

  const [currentIndex, setCurrentIndex] = useState(UNSELECTED_ITEM_INDEX);

  const handleTextChange = e => {
    onTextChanged(e.target.value);
  };

  const handleItemSelected = e => {
    const index = e.currentTarget.getAttribute('data-index');
    const item = data[index];
    onItemSelected(item);
  };

  const handleKeyDown = e => {
    const { value } = e.target;
    if (e.key === 'Enter' && currentIndex > -1) {
      onItemSelected(data[currentIndex]);
    } else if (e.key === 'Enter' && value) {
      onSubmit(value);
    } else if (e.key === 'Escape') {
      onTextChanged('');
    } else if (e.key === 'ArrowDown' && currentIndex < data.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else if (e.key === 'ArrowUp' && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') {
      setCurrentIndex(-1);
    }
  };

  const renderInput = () => {
    const color = hasError ? 'warning' : null;
    return (
      <InputGroup data-testid="Autocomplete" stretched color={color}>
        <Input
          data-testid="Autocomplete__Input"
          className="sm-autocomplete__input"
          value={text}
          onChange={handleTextChange}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
        />
        <Choose>
          <When condition={isLoading}>
            <InputGroupAddon>
              <ProgressCircle continuous size="xs" />
            </InputGroupAddon>
          </When>
          <When condition={hasError}>
            <InputGroupAddon>
              <IconWarning />
            </InputGroupAddon>
          </When>
          <When condition={!!icon}>
            <InputGroupAddon>{icon}</InputGroupAddon>
          </When>
        </Choose>
      </InputGroup>
    );
  };

  return (
    <div className="sm-autocomplete">
      <Menu
        data-testid="Autocomplete__Menu"
        dense
        noRules
        show={data.length > 0 && !!text}
        menuTrigger={renderInput()}
        stretched
      >
        {data.map((item, idx) => (
          <MenuItem
            data-testid="Autocomplete__MenuItem"
            key={item.id}
            onClick={handleItemSelected}
            isFocused={currentIndex === idx}
          >
            {renderItem(item)}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

Autocomplete.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired
    })
  ).isRequired,
  onTextChanged: PropTypes.func.isRequired,
  text: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  renderItem: PropTypes.func,
  onItemSelected: PropTypes.func,
  onSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
  icon: PropTypes.node,
  hasError: PropTypes.bool
};

Autocomplete.defaultProps = {
  placeholder: null,
  renderItem: ({ text }) => text,
  onSubmit: () => {},
  onItemSelected: () => {},
  isLoading: false,
  icon: <IconSearch />,
  hasError: false
};

export default Autocomplete;
